import React, { useEffect, useRef, useState } from 'react';
import { avatarFrames } from '../assets/avatar-selected/frames';
import './hero.css';
import usePortraitInteraction from '../hooks/usePortraitInteraction';

// Screen-coordinate unit vectors. Add diagonal vectors and mild-to-strong
// sequences here; selection and preloading automatically include them.
export const DIRECTIONS = {
  left: { vector: [-1, 0], frames: avatarFrames.left },
  right: { vector: [1, 0], frames: avatarFrames.right },
  up: { vector: [0, -1], frames: avatarFrames.up },
  down: { vector: [0, 1], frames: avatarFrames.down },
};
const CENTER = { direction: null, level: 0 };
const DEAD_ZONE = 0.14;
const STEP_MS = 45;
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const frameSource = ({ direction, level }) =>
  level === 0 ? avatarFrames.neutral : DIRECTIONS[direction].frames[level - 1];

export function selectPose(x, y, previous = CENTER) {
  const magnitude = Math.max(Math.abs(x), Math.abs(y));
  if (magnitude < DEAD_ZONE - 0.035) return CENTER;
  if (previous.level === 0 && magnitude < DEAD_ZONE + 0.035) return CENTER;
  const length = Math.hypot(x, y) || 1;
  const score = (name) => {
    const [dx, dy] = DIRECTIONS[name].vector;
    return (x * dx + y * dy) / length;
  };
  let direction = Object.keys(DIRECTIONS).reduce((best, name) =>
    score(name) > score(best) ? name : best
  );
  if (previous.direction && score(direction) - score(previous.direction) < 0.12) {
    direction = previous.direction;
  }
  const count = DIRECTIONS[direction].frames.length;
  const strength = clamp((magnitude - DEAD_ZONE) / (1 - DEAD_ZONE), 0, 1) * count;
  let level = clamp(Math.ceil(strength), 1, count);
  if (direction === previous.direction && previous.level > 0 &&
      strength >= previous.level - 1 - 0.2 && strength <= previous.level + 0.2) {
    level = previous.level;
  }
  return { direction, level };
}

export function stepPose(current, target) {
  if (current.level > 0 && current.direction !== target.direction) {
    return current.level === 1 ? CENTER : { ...current, level: current.level - 1 };
  }
  if (current.level === target.level && current.direction === target.direction) return current;
  const level = current.level + Math.sign(target.level - current.level);
  return level === 0 ? CENTER : { direction: target.direction, level };
}

// Retain decoded Image objects across renders and StrictMode remounts.
let preloadPromise;
let preloadedImages = [];
function preloadFrames() {
  if (!preloadPromise) {
    const sources = [...new Set([avatarFrames.neutral,
      ...Object.values(DIRECTIONS).flatMap(({ frames }) => frames)])];
    preloadPromise = Promise.all(sources.map((src) => new Promise((resolve, reject) => {
      const image = new Image();
      preloadedImages.push(image);
      image.onload = async () => {
        try {
          if (image.decode) await image.decode();
          resolve();
        } catch (error) { reject(error); }
      };
      image.onerror = reject;
      image.src = src;
    }))).catch((error) => {
      preloadPromise = null;
      preloadedImages = [];
      throw error;
    });
  }
  return preloadPromise;
}

export default function Hero({ portraitSrc, onExplore }) {
  const heroRef = useRef(null);
  usePortraitInteraction(heroRef, Boolean(portraitSrc));
  const pointerRef = useRef({ x: 0, y: 0 });
  const wakeRef = useRef(() => {});
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const [pose, setPose] = useState(CENTER);

  useEffect(() => {
    const desktop = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
    const touch = window.matchMedia('(any-pointer: coarse)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!portraitSrc && desktop.matches && !touch.matches && !reducedMotion.matches);
    const queries = [desktop, touch, reducedMotion];
    queries.forEach((query) => query.addEventListener('change', update));
    update();
    return () => queries.forEach((query) => query.removeEventListener('change', update));
  }, [portraitSrc]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    preloadFrames().then(() => {
      if (!cancelled) setReady(true);
    }).catch(() => {
      // Failed loading/decoding keeps the usable neutral portrait.
      if (!cancelled) setReady(false);
    });
    return () => { cancelled = true; };
  }, [enabled]);

  useEffect(() => {
    pointerRef.current = { x: 0, y: 0 };
    setPose(CENTER);
    if (!enabled || !ready) return undefined;
    let raf = null;
    let lastTime = 0;
    let lastStep = 0;
    let smooth = { x: 0, y: 0 };
    let desired = CENTER;
    let displayed = CENTER;
    const tick = (time) => {
      raf = null;
      const dt = lastTime ? Math.min(time - lastTime, 64) : 16;
      lastTime = time;
      const ease = 1 - Math.exp(-dt / 90);
      const target = pointerRef.current;
      smooth.x += (target.x - smooth.x) * ease;
      smooth.y += (target.y - smooth.y) * ease;
      const settled = Math.abs(target.x - smooth.x) + Math.abs(target.y - smooth.y) < 0.002;
      if (settled) smooth = { ...target };
      desired = selectPose(smooth.x, smooth.y, desired);
      if (time - lastStep >= STEP_MS) {
        const next = stepPose(displayed, desired);
        if (next !== displayed) {
          displayed = next;
          setPose(next);
        }
        lastStep = time;
      }
      if (!settled || displayed.level !== desired.level || displayed.direction !== desired.direction) {
        raf = requestAnimationFrame(tick);
      }
    };
    const wake = () => {
      if (raf === null) {
        lastTime = 0;
        raf = requestAnimationFrame(tick);
      }
    };
    wakeRef.current = wake;
    const reset = () => {
      pointerRef.current = { x: 0, y: 0 };
      wake();
    };
    window.addEventListener('blur', reset);
    window.addEventListener('resize', reset);
    document.addEventListener('visibilitychange', reset);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
      wakeRef.current = () => {};
      window.removeEventListener('blur', reset);
      window.removeEventListener('resize', reset);
      document.removeEventListener('visibilitychange', reset);
    };
  }, [enabled, ready]);

  const returnToCenter = () => {
    pointerRef.current = { x: 0, y: 0 };
    wakeRef.current();
  };
  const followPointer = (event) => {
    if (event.pointerType === 'touch') {
      setEnabled(false);
      return;
    }
    if (!enabled || !ready || event.pointerType !== 'mouse') return;
    const bounds = heroRef.current.getBoundingClientRect();
    pointerRef.current = {
      x: clamp((event.clientX - bounds.left - bounds.width / 2) / (bounds.width / 2), -1, 1),
      y: clamp((event.clientY - bounds.top - bounds.height / 2) / (bounds.height / 2), -1, 1),
    };
    wakeRef.current();
  };
  const visiblePose = enabled && ready ? pose : CENTER;
  return (
    <section className={`hero${portraitSrc ? ' hero--portrait' : ''}`} ref={heroRef} aria-label="Avatar portrait"
      onPointerMove={followPointer} onPointerDown={followPointer}
      onPointerLeave={returnToCenter} onPointerCancel={returnToCenter}>
      {portraitSrc && (
        <>
          <div className="hero__ambient" aria-hidden="true" />
          <div className="hero__intro">
            <h1 tabIndex="-1">Zeina Tamer</h1>
            <p>UX/UI Designer, Visual<br className="hero__desktop-break" /> Creator &amp; Digital Storyteller</p>
          </div>
          <p className="hero__statement">Driven by psychology and<br className="hero__desktop-break" /> impact</p>
          <a className="hero__scroll" href="#projects" aria-label="Explore featured projects" onClick={(event) => {
            if (onExplore) { event.preventDefault(); onExplore(); }
          }}><span>SCROLL TO EXPLORE</span><span aria-hidden="true">↓︎</span></a>
        </>
      )}
      <div className="hero__avatar">
        {portraitSrc && <><span className="hero__portrait-orbit" aria-hidden="true" /><span className="hero__portrait-star hero__portrait-star--one" aria-hidden="true">✧︎</span><span className="hero__portrait-star hero__portrait-star--two" aria-hidden="true">✳︎</span></>}
        <div className="hero__portrait-motion">
        <img className="hero__avatar-frame" src={portraitSrc || frameSource(visiblePose)}
          alt={portraitSrc ? 'Illustrated portrait of Zeina' : 'Illustrated portrait'}
          width={portraitSrc ? 1080 : 1280} height={portraitSrc ? 1448 : 720}
          loading="eager" decoding="sync" fetchPriority="high" draggable={false}
          data-direction={visiblePose.direction || 'neutral'} data-level={visiblePose.level} />
        </div>
      </div>
    </section>
  );
}
